import { Component, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdminService } from '../admin.service';
import { CrudHelper } from '../../firebase/helpers/crudHelper';
import { ToastController, AlertController } from '@ionic/angular';
import { AngularFirestore } from '@angular/fire/compat/firestore';

@Component({
  selector: 'app-user-generated-content',
  templateUrl: './user-generated-content.component.html',
  styleUrls: ['./user-generated-content.component.scss'],
})
export class UserGeneratedContentComponent implements OnInit {
  flagData: any[] = [];
  comments: any[] = [];
  events: any[] = [];
  activeTab = 'all';
  selectedCommentId: string = null;
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    private crudHelper: CrudHelper,
    private toastController: ToastController,
    private alertController: AlertController,
    private fireStore: AngularFirestore
  ) {}

  ngOnInit() {
    this.loadFlaggedContent();
  }

  loadFlaggedContent() {
    this.adminService
      .getFlaggedContent()
      .pipe(
        takeUntil(this.destroy$),
        map(items => items)
      )
      .subscribe((data: any[]) => {
        this.flagData = data.reverse();
        // Filter out comments and events
        this.comments = this.flagData.filter(item => item.type === 'comment');
        this.events = this.flagData.filter(
          item => item.type === 'event' || !item.type
        );
      });
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  // Method to navigate directly to comments tab
  navigateToCommentsTab(comment = null) {
    this.activeTab = 'comments';
    this.setActiveTab('comments');

    // If we have a specific comment, store its ID and scroll to it
    if (comment) {
      this.selectedCommentId = comment.flagContentId || comment.id;

      // Use setTimeout to ensure the DOM has updated with the tab change
      setTimeout(() => {
        const commentElement = document.getElementById(
          `comment-${this.selectedCommentId}`
        );
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }

  // Check if a comment is selected
  isCommentSelected(comment): boolean {
    return this.selectedCommentId === (comment.flagContentId || comment.id);
  }

  async approveComment(comment) {
    try {
      // Update the status in flaggedContent collection
      await this.fireStore
        .collection('flaggedContent')
        .doc(comment.flagContentId || comment.id)
        .update({
          status: 'approved',
        });

      // Remove from flagged content lists
      this.removeFromLists(comment);

      this.presentToast('Comment has been approved');
    } catch (error) {
      console.error('Error approving comment:', error);
      this.presentToast('Error approving comment', 'danger');
    }
  }

  async rejectComment(comment) {
    try {
      // First update the flaggedContent entry
      await this.fireStore
        .collection('flaggedContent')
        .doc(comment.flagContentId || comment.id)
        .update({
          status: 'rejected',
        });

      // Then delete/mark the actual comment as deleted (if commentId exists)
      if (comment.commentId) {
        await this.fireStore
          .collection('comments')
          .doc(comment.commentId)
          .update({
            isDeleted: true,
            text: 'This comment has been removed by a moderator',
          });
      }

      // Remove from flagged content lists
      this.removeFromLists(comment);

      this.presentToast('Comment has been rejected and removed');
    } catch (error) {
      console.error('Error rejecting comment:', error);
      this.presentToast('Error rejecting comment', 'danger');
    }
  }

  async confirmRejectComment(comment) {
    const alert = await this.alertController.create({
      header: 'Confirm Rejection',
      message: 'Are you sure you want to reject and remove this comment?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Reject Comment',
          handler: () => {
            this.rejectComment(comment);
          },
        },
      ],
    });

    await alert.present();
  }

  removeFromLists(item) {
    // Remove from appropriate arrays
    this.flagData = this.flagData.filter(
      flagItem =>
        (flagItem.flagContentId || flagItem.id) !==
        (item.flagContentId || item.id)
    );

    if (item.type === 'comment') {
      this.comments = this.comments.filter(
        comment =>
          (comment.flagContentId || comment.id) !==
          (item.flagContentId || item.id)
      );
    } else {
      this.events = this.events.filter(
        event =>
          (event.flagContentId || event.id) !== (item.flagContentId || item.id)
      );
    }
  }

  deleteContent(item) {
    this.crudHelper.deleteHelper({
      collectionName: 'flaggedContent',
      id: item.flagContentId || item.id,
    });

    this.removeFromLists(item);

    this.presentToast('Content has been deleted');
  }

  async presentToast(message: string, color: string = 'success') {
    const toast = await this.toastController.create({
      message,
      duration: 2000,
      position: 'bottom',
      color: color,
    });
    toast.present();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

